import React, { useMemo } from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import {
  AgentModel, AgentModelCategory, ModelCategoryType, UsedModel,
} from '$/domains/agentModel/type';
import styles from './index.module.scss';
import CommonExposure from '../CommonExposure';

/** 渲染模型分类和模型列表，并将显式模型选择交给外层受控状态。 */
function ModelSelectPopup({
  modelMap,
  usedModel,
  setUsedModel,
  onExposed,
  onModelClick,
  onModelSelect,
  onBeforeModelChange,
  variant = 'default',
}: {
  modelMap: {
    imageModel: AgentModelCategory;
    videoModel: AgentModelCategory;
  };
  usedModel: UsedModel;
  setUsedModel: (usedModel: UsedModel) => void;
  onExposed?: (params: { modelCategoryTitle: string; model?: AgentModel; }) => void;
  onModelClick?: (params: { modelCategoryTitle: string; model?: AgentModel; }) => void;
  /** 模型完成选择后的回调，用于外层关闭选择浮层。 */
  onModelSelect?: (model: AgentModel) => void;
  /** 模型切换前的拦截回调。返回 false 或调用 reject 阻止切换。 */
  onBeforeModelChange?: (model: AgentModel, proceed: () => void) => void;
  /** 首页输入工具栏使用新版固定高度面板，其他入口保留原样式。 */
  variant?: 'default' | 'inputPanel';
}) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isInputPanel = variant === 'inputPanel';
  const activeTab = useMemo(() => {
    return usedModel.activeType || 'imageModel';
  }, [usedModel]);
  //  高度自适应展示两个tab最高的
  const height = useMemo(() => {
    const num = Math.max(modelMap.imageModel.modelItems.length, modelMap.videoModel.modelItems.length);
    // 默认有一个自动选项所以需要+1
    return 60 * (num + 1);
  }, [modelMap]);

  /** 切换模型分类时仅更新当前分类，不触发完成选择。 */
  const tabButtonClicked = (tab: ModelCategoryType) => {
    setUsedModel({
      ...usedModel,
      activeType: tab,
    });
  };

  /** 选择具体模型后更新受控值，并通知工具栏关闭浮层。 */
  const buttonClicked = (item: AgentModel) => {
    /** 执行通过前置拦截后的模型切换。 */
    const doChange = () => {
      setUsedModel({
        ...usedModel,
        [activeTab]: item,
      });
      onModelClick && onModelClick({ modelCategoryTitle: activeTab, model: item });
      onModelSelect?.(item);
    };

    if (onBeforeModelChange) {
      onBeforeModelChange(item, doChange);
    } else {
      doChange();
    }
  };
  return (
    <div className={classNames(styles.ModelSelectPopup, isInputPanel && styles.inputPanel)}>
      <div className={styles.title}>
        {languageStore.smartcatTranslationText((v) => v.roboneo_model)}
      </div>
      <div className={styles.tabBox}>
        {Object.keys(modelMap).map((item) => (
          <div
            key={`${item}-tab`}
            onClick={() => {
              tabButtonClicked(item as ModelCategoryType);
            }}
            className={classNames([styles.tab, item === activeTab ? styles.active : ''])}
          >
            {modelMap[item as (keyof typeof modelMap)].modelCategoryTitle}
          </div>
        ))}
      </div>
      <div
        className={styles.container}
        style={isInputPanel ? undefined : {
          height: `${height}px`,
        }}
      >
        {
        Object.keys(modelMap).map((model) => (
          <div key={`${model}-list`} className={classNames([styles.list, model === activeTab ? styles.active : ''])}>
            <CommonExposure
              onlyOnce={false}
              onClick={() => { buttonClicked({
                modelDesc: '',
                modelName: '',
                modelValue: '',
                enable: true,
              }); }}
              className={styles.item}
              onExposed={() => {
                onExposed && onExposed({
                  modelCategoryTitle: model,
                });
              }}
            >
              <div className={styles.content}>
                <div className={styles.nameBox}>
                  <div className={styles.name}>{languageStore.smartcatTranslationText((v) => v.roboneo_auto)}</div>
                </div>
                <div className={styles.desc}>{languageStore.smartcatTranslationText((v) => v.roboneo_model_auto_describe)}</div>

              </div>
              <div
                className={classNames([styles.button, (!(usedModel[activeTab as keyof typeof usedModel] as AgentModel).modelValue) ? styles.active : ''])}
              >
                <IconFont name="checkBoxMark" fontSize={10} />
              </div>
            </CommonExposure>
            {modelMap[model as (keyof typeof modelMap)].modelItems.map((item) => {
              if (item.enable) {
                return (
                  <CommonExposure
                    onlyOnce={false}
                    onExposed={() => {
                      onExposed && onExposed({
                        modelCategoryTitle: model,
                        model: item,
                      });
                    }}
                    onClick={() => { buttonClicked(item); }}
                    className={styles.item}
                    key={item.modelValue}
                  >
                    <div className={styles.content}>
                      <div className={styles.nameBox}>
                        <div className={styles.name}>{item.modelName}</div>
                        <div className={styles.tip}>
                          {item.badgeUrl ? (
                            <img
                              src={item.badgeUrl}
                              alt=""
                              className={styles.img}
                              crossOrigin="anonymous"
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className={styles.desc}>{item.modelDesc}</div>

                    </div>
                    <div
                      className={classNames([styles.button, (usedModel[activeTab as keyof typeof usedModel] as AgentModel).modelValue === item.modelValue ? styles.active : ''])}
                    >
                      <IconFont name="checkBoxMark" fontSize={10} />
                    </div>
                  </CommonExposure>
                );
              }
              return null;
            })}

          </div>
        ))
       }
      </div>
    </div>
  );
}

export default ModelSelectPopup;
